import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import AboutSubNav from '../../components/AboutSubNav';
import { HISTORY_TIMELINE } from '../../mock/mock';

const REVEAL_INTERVAL_MS = 1000; // 1 sec pose between each entry

/**
 * Presentation-style timeline row driven by `revealed` prop from the parent auto-sequencer.
 * Even index → year slides in from FAR LEFT, description from FAR RIGHT.
 * Odd  index → description slides in from FAR LEFT, year from FAR RIGHT.
 */
const TimelineRow = ({ h, i, revealed }) => {
  const yearEven = i % 2 === 0;

  return (
    <div className="relative pl-14 md:pl-0 md:grid md:grid-cols-2 md:gap-12 mb-16 md:mb-20">
      {/* Central dot */}
      <div
        className={`absolute left-4 md:left-1/2 top-2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-[#a67c3d] ring-4 ring-[#faf6f0] transition-transform duration-700 ${
          revealed ? 'scale-100' : 'scale-0'
        }`}
        style={{ transitionDelay: '200ms' }}
      />

      {/* YEAR column */}
      <div
        className={`transition-all duration-[900ms] ease-out ${
          yearEven ? 'md:text-right md:pr-14' : 'md:col-start-2 md:pl-14'
        } ${
          revealed
            ? 'opacity-100 translate-x-0'
            : yearEven
            ? 'opacity-0 -translate-x-[100vw] md:-translate-x-[60vw]'
            : 'opacity-0 translate-x-[100vw] md:translate-x-[60vw]'
        }`}
      >
        <p className="font-serif text-4xl md:text-6xl text-[#a67c3d] leading-none tracking-tight">
          {h.year}
        </p>
      </div>

      {/* CONTENT column */}
      <div
        className={`mt-4 md:mt-0 transition-all duration-[900ms] ease-out ${
          yearEven ? 'md:col-start-2 md:pl-14' : 'md:row-start-1 md:pr-14 md:text-right'
        } ${
          revealed
            ? 'opacity-100 translate-x-0'
            : yearEven
            ? 'opacity-0 translate-x-[100vw] md:translate-x-[60vw]'
            : 'opacity-0 -translate-x-[100vw] md:-translate-x-[60vw]'
        }`}
        style={{ transitionDelay: '180ms' }}
      >
        <h3 className="font-serif text-2xl md:text-3xl text-[#1a1614] mb-3">{h.title}</h3>
        <p className="text-[#4a3f36] leading-relaxed max-w-md md:max-w-none md:ml-auto md:mr-0">
          {h.desc}
        </p>
      </div>
    </div>
  );
};

const History = () => {
  const containerRef = useRef(null);
  const [revealedIndex, setRevealedIndex] = useState(-1);
  const startedRef = useRef(false);

  // Once the timeline enters view, auto-reveal each row with a 1s pause
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          io.unobserve(el);

          // Reveal first row immediately, then step every 1s
          setRevealedIndex(0);
          let i = 1;
          const timer = setInterval(() => {
            if (i >= HISTORY_TIMELINE.length) {
              clearInterval(timer);
              return;
            }
            setRevealedIndex(i);
            i += 1;
          }, REVEAL_INTERVAL_MS);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -80px 0px' }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div>
      <section className="pt-24 pb-16 bg-[#f2ebde]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-5">History</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#1a1614] leading-[1.05] max-w-4xl">
            A family enterprise, formalised for a <span className="italic text-[#a67c3d]">new global manufacturing era.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#4a3f36] leading-relaxed">
            Across three generations, each has widened the scope of the enterprise &mdash; the first established it, the second transformed it into an export-led furniture manufacturer, and the third is expanding its global capabilities and building its hospitality business.
          </p>
        </div>
      </section>

      <AboutSubNav />

      <section className="py-24 md:py-28 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div ref={containerRef} className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-[#e0d6c5]" />
            {HISTORY_TIMELINE.map((h, i) => (
              <TimelineRow key={h.year} h={h} i={i} revealed={i <= revealedIndex} />
            ))}
          </div>

          {/* Spinning brand seal — end of Legacy */}
          <div className="pt-12 md:pt-16 pb-4 flex flex-col items-center">
            <div className="relative w-72 h-72 md:w-[380px] md:h-[380px] flex items-center justify-center">
              <svg
                viewBox="0 0 300 300"
                className="absolute inset-0 w-full h-full rcd-logo-spin rcd-logo-spin--slow"
                aria-hidden="true"
              >
                <defs>
                  <path
                    id="rcd-seal-arc-history"
                    d="M 150,150 m -125,0 a 125,125 0 1,1 250,0 a 125,125 0 1,1 -250,0"
                  />
                </defs>
                <text
                  fill="#5c3a10"
                  fontFamily="'Playfair Display', Georgia, serif"
                  fontSize="20"
                  fontWeight="700"
                  letterSpacing="5"
                  style={{ textTransform: 'uppercase' }}
                >
                  <textPath href="#rcd-seal-arc-history" startOffset="0%">
                    Rajdhani Crafts &amp; Daughters &nbsp;&middot;&nbsp;
                  </textPath>
                </text>
              </svg>

              <div className="absolute inset-10 md:inset-12 rounded-full border-2 border-[#a67c3d]/50" />

              {/* Center: real RCD logo */}
              <div className="relative z-10 w-36 h-36 md:w-44 md:h-44 rounded-full bg-[#faf6f0] flex items-center justify-center shadow-inner">
                <img
                  src="https://customer-assets-rejwkqb3.emergentagent.net/job_rcd-clients/artifacts/jboyka5i_RCD%20logo.jpg"
                  alt="RCD - Rajdhani Crafts and Daughters"
                  className="w-32 h-32 md:w-40 md:h-40 object-contain"
                  style={{ mixBlendMode: 'multiply', filter: 'brightness(1.03) contrast(1.06)' }}
                />
              </div>
            </div>
            <p className="mt-8 text-xs md:text-sm tracking-[0.28em] uppercase text-[#a67c3d]">
              Three generations. One workshop.
            </p>
          </div>

          <div className="mt-16 flex flex-wrap justify-between items-center gap-6 pt-10 border-t border-[#e0d6c5]">
            <Link to="/about" className="rcd-link text-sm text-[#1a1614]">&larr; Back to About</Link>
            <Link to="/about/founder" className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a1614] text-[#faf6f0] hover:bg-[#a67c3d] transition-colors text-sm">
              Meet the Founders <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default History;
